import { getIntlLocale } from '@bofrak-backend/shared-i18n';
import {
  defaultImage,
  PurchaseOrder,
  PurchaseOrderEstimateStatus,
  StoreProduct,
  PurchaseOrderPaymentStatus,
  PurchaseStatus,
  formatCurrency,
} from '@bofrak-backend/shared';
import { DatePicker, Loader } from '../../ui';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  HStack,
  Image,
  Heading,
  Progress,
  SimpleGrid,
  Skeleton,
  Stat,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  StatHelpText,
  StatLabel,
  StatNumber,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Text,
  VStack,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { endOfDay } from 'date-fns';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';
import { apiAdapter } from '../api/backend';
import { useStoreProducts } from '../../store-products/hooks/use-store-products';
import { envVars } from '../utils/constants';
import Main from '../components/Main';
import { dateRangeAtom, merchantAtom, storeAtom } from '../recoil/atoms';
import { useSharedUiTranslation } from '../../i18n';
import {
  getPurchasePaymentStatusLabel,
  getPurchaseStatusLabel,
} from '../utils/status-labels';

interface PurchasedItem {
  item_id: string;
  item_name: string;
  received: number;
  total_quantity: number;
  cancelled: number;
  value: number;
  cost: number;
  percentage_received: number;
  /** Σ of the item's saved line estimates (profit estimation setting). */
  min_profit: number;
  max_profit: number;
  has_estimate: boolean;
  pending_estimates: number;
  /** How many purchase orders of the period bought this item. */
  po_count: number;
}

interface PurchaseSummary {
  total_received: number;
  total_value: number;
  total_ordered: number;
  total_cancelled: number;
  final_value: number;
  percentage_received: number;
  total_cancelled_value: number;
  total_received_value: number;
  count: number;
  items: PurchasedItem[];
  purchase_orders: number;
  min_profit: number;
  max_profit: number;
  has_estimates: boolean;
  pending_estimates: number;
}

interface SummaryMetricProps {
  label: string;
  value: number | string;
  color?: string;
  format?: 'currency' | 'number' | 'text';
  helpText?: string;
  isLoading?: boolean;
}

interface PurchasedItemsTableProps {
  items: PurchasedItem[];
  products: Map<string, StoreProduct>;
  onSelect: (item: PurchasedItem) => void;
}

const money = (value: number) => formatCurrency(value, true);

/** Quantities: at most 2 decimals, trailing zeros dropped (2.5, 3, 1.23). */
const qty = (value: number) =>
  (value ?? 0).toLocaleString(getIntlLocale(), { maximumFractionDigits: 2 });

/** For `count` values, which i18next needs as numbers for plurals. */
const round2 = (value: number) => Math.round((value ?? 0) * 100) / 100;

/** One row per purchased item, as in the Purchase History mock-up. */
const PurchasedItemsTable: React.FC<PurchasedItemsTableProps> = ({
  items,
  products,
  onSelect,
}) => {
  const { t } = useSharedUiTranslation();
  const open = (item: PurchasedItem) => () => onSelect(item);
  return (
    <TableContainer w="full" overflowX="hidden">
      <Table
        size="sm"
        variant="simple"
        w="full"
        sx={{
          // Mobile first: five columns fit a phone without sideways scroll.
          tableLayout: 'fixed',
          th: { px: 1, py: 2, fontSize: '2xs', whiteSpace: 'normal' },
          td: { px: 1, py: 2, whiteSpace: 'normal', verticalAlign: 'middle' },
        }}>
        <Thead>
          <Tr>
            <Th w="34%">{t('Product')}</Th>
            <Th w="18%" isNumeric>
              {t('Received / Ordered')}
            </Th>
            <Th w="18%" isNumeric>
              {t('Unit / Received')}
            </Th>
            <Th w="17%" isNumeric>
              {t('Min / Max')}
            </Th>
            <Th w="13%" isNumeric>
              {t("Num of PO's")}
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.map((item) => {
            const product = products.get(item.item_id);
            const unit = product?.unit ? String(product.unit) : '';
            return (
              <Tr
                key={item.item_id}
                cursor="pointer"
                tabIndex={0}
                role="button"
                aria-label={t('Purchase orders for {{name}}', {
                  name: item.item_name,
                })}
                _hover={{ bg: 'gray.50' }}
                _focusVisible={{
                  bg: 'gray.50',
                  outline: '2px solid',
                  outlineColor: 'blue.400',
                }}
                onClick={open(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(item);
                  }
                }}>
                <Td>
                  <HStack spacing={2}>
                    <Image
                      src={product?.image || defaultImage}
                      alt=""
                      boxSize={{ base: '32px', md: '40px' }}
                      objectFit="contain"
                      borderRadius="md"
                      flexShrink={0}
                      loading="lazy"
                    />
                    <VStack align="start" spacing={0} minW={0}>
                      <Text fontSize="xs" fontWeight="semibold" noOfLines={2}>
                        {item.item_name}
                      </Text>
                      {item.cancelled > 0 && (
                        <Badge colorScheme="red" fontSize="xx-small">
                          {t('Cancelled {{count}}', {
                            count: round2(item.cancelled),
                          })}
                        </Badge>
                      )}
                    </VStack>
                  </HStack>
                </Td>
                <Td isNumeric>
                  <Text fontSize="xs">
                    {qty(item.received)} / {qty(item.total_quantity)}
                  </Text>
                  {unit && (
                    <Text fontSize="xs" color="gray.500">
                      {unit}
                    </Text>
                  )}
                </Td>
                <Td isNumeric>
                  <Text fontSize="xs">{money(item.cost)}</Text>
                  <Text fontSize="xs" color="gray.600">
                    {money(item.received * item.cost)}
                  </Text>
                </Td>
                <Td isNumeric>
                  {item.has_estimate ? (
                    <>
                      <Text
                        fontSize="xs"
                        color="orange.600"
                        fontWeight="semibold">
                        {money(item.min_profit)}
                      </Text>
                      <Text
                        fontSize="xs"
                        color="green.600"
                        fontWeight="semibold">
                        {money(item.max_profit)}
                      </Text>
                    </>
                  ) : item.pending_estimates > 0 ? (
                    <Text fontSize="xs" color="orange.600">
                      {t('Awaiting prices')}
                    </Text>
                  ) : (
                    <Text fontSize="xs" color="gray.400">
                      —
                    </Text>
                  )}
                </Td>
                <Td isNumeric>
                  <Text fontSize="xs" fontWeight="semibold" color="blue.600">
                    {item.po_count}
                  </Text>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

interface ItemPurchaseOrdersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: PurchasedItem | null;
  purchaseOrders: PurchaseOrder[];
}

const SummaryMetric: React.FC<SummaryMetricProps> = ({
  label,
  value,
  color = 'gray.700',
  format = 'number',
  helpText,
  isLoading = false,
}) => {
  const formattedValue = useMemo(() => {
    if (format === 'currency') {
      return formatCurrency(value as number, true);
    }
    if (format === 'text') {
      return value;
    }
    return typeof value === 'number' ? value.toLocaleString() : value;
  }, [value, format]);

  return (
    <Stat textAlign="center">
      <StatLabel fontSize="xs" color="gray.600">
        {label}
      </StatLabel>
      <Skeleton isLoaded={!isLoading}>
        <StatNumber fontSize="md" color={color}>
          {formattedValue}
        </StatNumber>
      </Skeleton>
      {helpText && (
        <StatHelpText fontSize="xs" color="gray.500">
          {helpText}
        </StatHelpText>
      )}
    </Stat>
  );
};

const STATUS_COLOR_MAP: Record<PurchaseStatus, string> = {
  [PurchaseStatus.DRAFT]: 'gray',
  [PurchaseStatus.PENDING]: 'orange',
  [PurchaseStatus.CLOSED]: 'green',
  [PurchaseStatus.CANCELLED]: 'red',
};

const PAYMENT_STATUS_COLOR_MAP: Record<PurchaseOrderPaymentStatus, string> = {
  [PurchaseOrderPaymentStatus.UNPAID]: 'red',
  [PurchaseOrderPaymentStatus.PARTIALLY_PAID]: 'orange',
  [PurchaseOrderPaymentStatus.PAID]: 'green',
};

const ItemPurchaseOrdersDrawer: React.FC<ItemPurchaseOrdersDrawerProps> = ({
  isOpen,
  onClose,
  item,
  purchaseOrders,
}) => {
  const { t } = useSharedUiTranslation();
  const drawerSize = useBreakpointValue({ base: 'full', md: 'lg' });

  const sourcePOs = useMemo(() => {
    if (!item) return [];

    const priorityOrder: Record<string, number> = {
      [PurchaseOrderPaymentStatus.UNPAID]: 0,
      [PurchaseOrderPaymentStatus.PARTIALLY_PAID]: 1,
    };
    const statusPriority: Record<string, number> = {
      [PurchaseStatus.PENDING]: 2,
    };

    return purchaseOrders
      .filter((po) => po.items.some((i) => i.id === item.item_id))
      .sort((a, b) => {
        const aPriority =
          priorityOrder[a.payment_status] ??
          statusPriority[a.purchase_status] ??
          3;
        const bPriority =
          priorityOrder[b.payment_status] ??
          statusPriority[b.purchase_status] ??
          3;

        if (aPriority !== bPriority) return aPriority - bPriority;

        return (
          new Date(b.purchase_date).getTime() -
          new Date(a.purchase_date).getTime()
        );
      });
  }, [item, purchaseOrders]);

  if (!item) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size={drawerSize}
      placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" py={3}>
          <VStack align="start" spacing={1}>
            <Text fontSize="md" fontWeight="bold" noOfLines={2}>
              {item.item_name}
            </Text>
            <HStack spacing={3} fontSize="xs" color="gray.600">
              <Text>
                {t('Ordered: {{total}}', { total: qty(item.total_quantity) })}
              </Text>
              <Text color="green.600">
                {t('Received: {{total}}', { total: qty(item.received) })}
              </Text>
              <Text color="red.600">
                {t('Cancelled: {{total}}', { total: qty(item.cancelled) })}
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {t('Found in {{count}} purchase order', {
                count: sourcePOs.length,
              })}
            </Text>
          </VStack>
        </DrawerHeader>
        <DrawerBody py={4}>
          <VStack spacing={3} align="stretch">
            {sourcePOs.map((po) => {
              const poItem = po.items.find((i) => i.id === item.item_id);
              if (!poItem) return null;
              const activeQty = poItem.quantity - poItem.cancelled;
              const itemFulfillment = Math.min(
                100,
                activeQty > 0 ? (poItem.received / activeQty) * 100 : 0,
              );
              const statusColor = STATUS_COLOR_MAP[po.purchase_status];

              return (
                <Card
                  key={po.id}
                  size="sm"
                  variant="outline"
                  overflow="hidden"
                  borderLeftWidth="4px"
                  borderLeftColor={`${statusColor}.400`}>
                  {/* Header band: supplier + date, then PO number + statuses */}
                  <Box
                    bg={`${statusColor}.50`}
                    px={3}
                    py={2}
                    borderBottomWidth="1px">
                    <HStack justify="space-between" align="start" spacing={2}>
                      <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                        {po.supplier_name || '—'}
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                        flexShrink={0}>
                        {new Date(po.purchase_date).toLocaleDateString(
                          getIntlLocale(),
                          { day: 'numeric', month: 'short', year: 'numeric' },
                        )}
                      </Text>
                    </HStack>
                    <HStack spacing={2} mt={1} flexWrap="wrap">
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="gray.600"
                        title={po.id}>
                        #{po.id.slice(-6).toUpperCase()}
                      </Text>
                      <Badge colorScheme={statusColor} fontSize="2xs">
                        {getPurchaseStatusLabel(po.purchase_status)}
                      </Badge>
                      <Badge
                        colorScheme={
                          PAYMENT_STATUS_COLOR_MAP[po.payment_status]
                        }
                        fontSize="2xs">
                        {getPurchasePaymentStatusLabel(po.payment_status)}
                      </Badge>
                    </HStack>
                  </Box>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      {/* Item quantities in this PO */}
                      <Box>
                        <SimpleGrid columns={3} spacing={2}>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="gray.600">
                              {t('Ordered')}
                            </Text>
                            <Text fontSize="md" fontWeight="bold">
                              {qty(poItem.quantity)}
                            </Text>
                          </VStack>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="green.600">
                              {t('Received')}
                            </Text>
                            <Text
                              fontSize="md"
                              fontWeight="bold"
                              color="green.700">
                              {qty(poItem.received)}
                            </Text>
                          </VStack>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="red.600">
                              {t('Cancelled')}
                            </Text>
                            <Text
                              fontSize="md"
                              fontWeight="bold"
                              color={
                                poItem.cancelled > 0 ? 'red.600' : 'gray.400'
                              }>
                              {qty(poItem.cancelled)}
                            </Text>
                          </VStack>
                        </SimpleGrid>
                        <HStack spacing={2} mt={2}>
                          <Progress
                            value={itemFulfillment}
                            size="sm"
                            flex={1}
                            colorScheme={
                              itemFulfillment === 100 ? 'green' : 'blue'
                            }
                            borderRadius="md"
                          />
                          <Text fontSize="xs" color="gray.600" minW="9">
                            {Math.round(itemFulfillment)}%
                          </Text>
                        </HStack>
                        {poItem.estimate?.status ===
                          PurchaseOrderEstimateStatus.SAVED && (
                          <HStack justify="space-between" fontSize="xs" mt={2}>
                            <Text color="gray.500">
                              {t('Potential profit')}
                            </Text>
                            <Text fontWeight="semibold">
                              <Text as="span" color="orange.600">
                                {formatCurrency(
                                  poItem.estimate.min_profit,
                                  true,
                                )}
                              </Text>
                              {' – '}
                              <Text as="span" color="green.600">
                                {formatCurrency(
                                  poItem.estimate.max_profit,
                                  true,
                                )}
                              </Text>
                            </Text>
                          </HStack>
                        )}
                        {poItem.estimate?.status ===
                          PurchaseOrderEstimateStatus.PENDING && (
                          <Text fontSize="xs" color="orange.600" mt={2}>
                            {t('Selling prices awaiting confirmation')}
                          </Text>
                        )}
                      </Box>

                      <Divider />

                      {/* PO totals */}
                      <SimpleGrid columns={2} spacing={2} fontSize="xs">
                        <VStack align="start" spacing={0}>
                          <Text color="gray.500">{t('PO Total')}</Text>
                          <Text fontWeight="semibold">
                            {formatCurrency(po.total_cost, true)}
                          </Text>
                        </VStack>
                        <VStack align="start" spacing={0}>
                          <Text color="gray.500">{t('Amount Due')}</Text>
                          <Text
                            fontWeight="semibold"
                            color={
                              (po.po_amount_due ?? 0) > 0
                                ? 'orange.600'
                                : 'green.600'
                            }>
                            {formatCurrency(po.po_amount_due ?? 0, true)}
                          </Text>
                        </VStack>
                      </SimpleGrid>

                      {/* Note */}
                      {po.note ? (
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            {t('Note')}
                          </Text>
                          <Text fontSize="xs" noOfLines={2}>
                            {po.note}
                          </Text>
                        </Box>
                      ) : null}
                    </VStack>
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const getPurchaseOrdersSummary = (
  purchase_orders: PurchaseOrder[],
): PurchaseSummary => {
  if (!purchase_orders?.length) {
    return {
      total_received: 0,
      total_value: 0,
      total_ordered: 0,
      total_cancelled: 0,
      final_value: 0,
      percentage_received: 0,
      total_cancelled_value: 0,
      total_received_value: 0,
      count: 0,
      items: [],
      purchase_orders: 0,
      min_profit: 0,
      max_profit: 0,
      has_estimates: false,
      pending_estimates: 0,
    };
  }

  const allItems = purchase_orders.flatMap((po) => po.items);
  const groupedItems = _.groupBy(allItems, 'id');

  const items: PurchasedItem[] = Object.entries(groupedItems).map(
    ([id, itemGroup]) => {
      const firstItem = itemGroup[0];
      const totalQuantity = _.sumBy(itemGroup, 'quantity');
      const totalReceived = _.sumBy(itemGroup, 'received');
      const totalCancelled = _.sumBy(itemGroup, 'cancelled');
      const activeQuantity = totalQuantity - totalCancelled;
      const saved = itemGroup.filter(
        (i) => i.estimate?.status === PurchaseOrderEstimateStatus.SAVED,
      );

      return {
        item_id: id,
        item_name: firstItem.name,
        received: totalReceived,
        total_quantity: totalQuantity,
        cancelled: totalCancelled,
        value: activeQuantity * firstItem.cost,
        cost: firstItem.cost,
        percentage_received:
          activeQuantity > 0 ? (totalReceived / activeQuantity) * 100 : 0,
        min_profit: _.sumBy(saved, (i) => i.estimate?.min_profit ?? 0),
        max_profit: _.sumBy(saved, (i) => i.estimate?.max_profit ?? 0),
        has_estimate: saved.length > 0,
        pending_estimates: itemGroup.filter(
          (i) => i.estimate?.status === PurchaseOrderEstimateStatus.PENDING,
        ).length,
        po_count: itemGroup.length,
      };
    },
  );

  const sortedItems = _.orderBy(items, 'value', 'desc');

  const summary = {
    items: sortedItems,
    count: sortedItems.length,
    total_received: _.sumBy(sortedItems, 'received'),
    total_ordered: _.sumBy(sortedItems, 'total_quantity'),
    total_cancelled: _.sumBy(sortedItems, 'cancelled'),
    total_value: _.sumBy(
      sortedItems,
      (item) => item.total_quantity * item.cost,
    ),
    total_cancelled_value: _.sumBy(
      sortedItems,
      (item) => item.cancelled * item.cost,
    ),
    total_received_value: _.sumBy(
      sortedItems,
      (item) => item.received * item.cost,
    ),
    final_value: 0,
    percentage_received: 0,
    purchase_orders: purchase_orders.length,
    min_profit: _.sumBy(items, 'min_profit'),
    max_profit: _.sumBy(items, 'max_profit'),
    has_estimates: items.some((item) => item.has_estimate),
    pending_estimates: _.sumBy(items, 'pending_estimates'),
  };

  summary.final_value = summary.total_value - summary.total_cancelled_value;
  summary.percentage_received =
    summary.total_ordered > 0
      ? (summary.total_received /
          (summary.total_ordered - summary.total_cancelled)) *
        100
      : 0;

  return summary;
};

function PurchaseHistory() {
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);
  const toast = useToast();
  const { t } = useSharedUiTranslation();

  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<PurchasedItem | null>(null);
  // Images and units come from the locally cached store products (no
  // extra fetch per row).
  const { products: storeProducts } = useStoreProducts({
    storeId: store?.id || '',
    merchantId: merchant?.id || '',
    apiEndpoint: envVars.API_URL,
    includeDeleted: false,
    autoSync: true,
  });
  const productsById = useMemo(
    () => new Map(storeProducts.map((p) => [p.id, p])),
    [storeProducts],
  );
  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  const handleItemClick = useCallback(
    (item: PurchasedItem) => {
      setSelectedItem(item);
      onDrawerOpen();
    },
    [onDrawerOpen],
  );

  // Infinite query for purchase orders
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
    error,
  } = useInfiniteQuery(
    ['purchase-history', dateRange, merchant?.id, store?.id],
    async ({ pageParam }) => {
      if (!merchant?.id || !store?.id || !dateRange.start || !dateRange.end) {
        throw new Error('Missing required parameters');
      }

      return await apiAdapter.getAllPurchaseOrders(merchant.id, {
        merchant_id: merchant.id,
        store_id: store.id,
        by_store_and_date: true,
        start_date: dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString(),
        cursor: pageParam,
        limit: 250,
      });
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor || undefined,
      enabled:
        !!store?.id && !!merchant?.id && !!dateRange.start && !!dateRange.end,
      onError: (err) => {
        toast({
          title: t('Error loading purchase history'),
          description: (err as Error).message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  // Process all fetched data
  const allPurchaseOrders = useMemo(() => {
    return data?.pages.flatMap((page) => page.purchase_orders) || [];
  }, [data]);

  const summary = useMemo(() => {
    return getPurchaseOrdersSummary(allPurchaseOrders);
  }, [allPurchaseOrders]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return summary.items;

    return summary.items.filter((item) =>
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [summary.items, searchTerm]);

  const handleDateChange = useCallback((start: Date, end?: Date) => {
    setDateRange({
      start: start,
      end: end ? end : endOfDay(start),
    });
  }, []);

  // Calculate metrics
  const metrics = useMemo(() => {
    const activeItems = summary.total_ordered - summary.total_cancelled;
    // Stored lines can carry more received than ordered − cancelled (older
    // orders); the bar never goes past 100 % and the excess is called out.
    const fulfillmentRate =
      activeItems > 0
        ? Math.min(100, (summary.total_received / activeItems) * 100)
        : 0;
    const overReceived = Math.max(0, summary.total_received - activeItems);

    return {
      itemsPurchased: summary.count,
      purchaseOrders: summary.purchase_orders,
      cancelledValue: summary.total_cancelled_value,
      receivedValue: summary.total_received_value,
      finalValue: summary.final_value,
      fulfillmentRate,
      overReceived,
      totalReceived: summary.total_received,
      totalActive: activeItems,
    };
  }, [summary]);

  if (!merchant || !store) {
    return (
      <Main>
        <Center h="60vh">
          <VStack spacing={2}>
            <Text color="gray.500">
              {t('Please select a store to view purchase history')}
            </Text>
          </VStack>
        </Center>
      </Main>
    );
  }

  if (isError) {
    return (
      <Main>
        <Center h="60vh">
          <VStack spacing={4}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {t('Error: {{message}}', { message: (error as Error).message })}
            </Alert>
            <Button onClick={() => refetch()} colorScheme="blue">
              {t('Retry')}
            </Button>
          </VStack>
        </Center>
      </Main>
    );
  }

  return (
    <Main>
      <VStack spacing={6} w="full" align="stretch">
        {/* Header */}
        <Card>
          <CardHeader>
            <VStack spacing={3}>
              <Heading size="md">{t('Purchase History')}</Heading>
              <DatePicker
                isRange={true}
                initialDate={dateRange.start ?? undefined}
                initialEndDate={dateRange.end ?? undefined}
                onDateChosen={handleDateChange}
                fontSize={11}
              />
            </VStack>
          </CardHeader>
        </Card>

        {/* Summary Statistics */}
        <Card>
          <CardHeader pb={2}>
            <Heading size="sm">{t('Purchase Summary')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
                <SummaryMetric
                  label={t('Purchase Orders')}
                  value={metrics.purchaseOrders}
                  color="blue.600"
                  isLoading={isLoading}
                />
                <SummaryMetric
                  label={t('Items Purchased')}
                  value={metrics.itemsPurchased}
                  color="blue.600"
                  isLoading={isLoading}
                />
                <SummaryMetric
                  label={t('Received Value')}
                  value={metrics.receivedValue}
                  format="currency"
                  color="green.600"
                  isLoading={isLoading}
                />
                <SummaryMetric
                  label={t('Cancelled Value')}
                  value={metrics.cancelledValue}
                  format="currency"
                  color="red.600"
                  isLoading={isLoading}
                />
              </SimpleGrid>

              {hasNextPage && (
                <Alert status="info" borderRadius="md" fontSize="sm" py={2}>
                  <AlertIcon />
                  {t(
                    'Totals cover the {{count}} purchase orders loaded so far. Load more below to include the rest of the period.',
                    { count: summary.purchase_orders },
                  )}
                </Alert>
              )}

              {(summary.has_estimates || summary.pending_estimates > 0) && (
                <Box w="full" p={4} bg="green.50" borderRadius="md">
                  <SimpleGrid columns={2} spacing={4}>
                    <SummaryMetric
                      label={t('Min. potential profit')}
                      value={summary.min_profit}
                      format="currency"
                      color="orange.600"
                      isLoading={isLoading}
                    />
                    <SummaryMetric
                      label={t('Max. potential profit')}
                      value={summary.max_profit}
                      format="currency"
                      color="green.600"
                      isLoading={isLoading}
                    />
                  </SimpleGrid>
                  {summary.pending_estimates > 0 && (
                    <Text
                      fontSize="xs"
                      color="orange.600"
                      textAlign="center"
                      mt={1}>
                      {t('{{count}} line awaiting prices', {
                        count: summary.pending_estimates,
                      })}
                    </Text>
                  )}
                </Box>
              )}

              <Box w="full" p={4} bg="gray.50" borderRadius="md">
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {t('Fulfillment Progress')}
                  </Text>
                  <Text fontSize="sm">
                    {t('{{received}} of {{total}} items received', {
                      received: qty(metrics.totalReceived),
                      total: qty(metrics.totalActive),
                    })}
                    {metrics.overReceived > 0 && (
                      <Text as="span" color="orange.600">
                        {' '}
                        {t('({{count}} over the ordered quantity)', {
                          count: round2(metrics.overReceived),
                        })}
                      </Text>
                    )}
                  </Text>
                </HStack>
                <Progress
                  value={metrics.fulfillmentRate}
                  size="md"
                  colorScheme={
                    metrics.fulfillmentRate === 100 ? 'green' : 'blue'
                  }
                  borderRadius="md"
                />
                <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
                  {t('{{percent}}% Complete', {
                    percent: metrics.fulfillmentRate.toFixed(1),
                  })}
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Items Grid */}
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="sm">{t('Purchased Items')}</Heading>
              {filteredItems.length > 0 && (
                <Text fontSize="xs" color="gray.500">
                  {t('{{count}} item', { count: filteredItems.length })}
                  {summary.has_estimates
                    ? ` · ${t('Profit on received stock')}`
                    : ''}
                </Text>
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Center py={8}>
                <Loader />
              </Center>
            ) : filteredItems.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Text color="gray.500">
                    {searchTerm
                      ? t('No items match your search')
                      : t('No purchase history found')}
                  </Text>
                  {searchTerm && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSearchTerm('')}>
                      {t('Clear search')}
                    </Button>
                  )}
                </VStack>
              </Center>
            ) : (
              <VStack spacing={4}>
                <PurchasedItemsTable
                  items={filteredItems}
                  products={productsById}
                  onSelect={handleItemClick}
                />

                {hasNextPage && (
                  <Center pt={4}>
                    <Button
                      onClick={() => fetchNextPage()}
                      isLoading={isFetchingNextPage}
                      loadingText={t('Loading more...')}
                      colorScheme="blue"
                      variant="outline"
                      size="sm">
                      {t('Load More Purchase Orders')}
                    </Button>
                  </Center>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>

      <ItemPurchaseOrdersDrawer
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        item={selectedItem}
        purchaseOrders={allPurchaseOrders}
      />
    </Main>
  );
}

export default PurchaseHistory;
